/**
 * Pricing Process
 * 
 * Displays pricing page with plan comparison.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { createCheckoutSession } from '../../services/subscription/api.js';
import { getJWTToken } from '../../services/auth/token-storage.js';
import { 
  designTokens, 
  createHeading, 
  createButton,
  createContainer,
  createScrollableContainer
} from '../../core/design-system.js';

interface PricingState {
  loading: boolean;
  error: string | null;
}

export class PricingProcess extends BaseProcess {
  kind: string = 'pricing';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'utility' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: PricingState = {
    loading: false,
    error: null
  };

  private pricingView: PricingView | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[PricingProcess] Starting pricing process ${this.id}`);
    
    // Listen for pricing.show event
    bus.on('pricing.show', () => {
      this.showPricing(ctx);
    });
    
    // Check URL for /pricing route
    if (window.location.pathname.includes('/pricing')) {
      this.showPricing(ctx);
    }
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private showPricing(ctx: ProcessContext): void {
    if (this.pricingView) {
      // Already showing, just ensure it's visible
      return;
    }
    
    const view = new PricingView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.pricingView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  async subscribeToPlan(planSlug: 'regular' | 'pro'): Promise<void> {
    const token = getJWTToken();
    if (!token) {
      // Redirect to login, then back to pricing
      window.location.href = '/login?redirect=/pricing';
      return;
    }

    this.state.loading = true;
    if (this.pricingView) {
      this.pricingView.updateState(this.state);
    }

    const response = await createCheckoutSession(planSlug);
    
    this.state.loading = false;
    
    if (response.success && response.data?.checkoutUrl) {
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl;
    } else {
      this.state.error = response.error || 'Failed to create checkout session';
      if (this.pricingView) {
        this.pricingView.updateState(this.state);
      }
    }
  }
}

/**
 * Pricing View
 * 
 * Displays pricing plans with comparison table.
 */
class PricingView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: PricingState,
    private process: PricingProcess
  ) {
    super(processId, viewId, {
      sizes: [1, 3], // Full height, normal width (default); full height, double width (alternative)
      showDismissButton: true,
      resizable: true,
    });
  }

  protected getContentHTML(): string {
    const heading = createHeading(1, 'Choose Your Plan');
    
    if (this.state.error) {
      const errorDisplay = `<div style="
        padding: ${designTokens.padding.md};
        margin-bottom: ${designTokens.padding.md};
        background: rgba(255, 0, 0, 0.1);
        border: 1px solid rgba(255, 0, 0, 0.3);
        border-radius: ${designTokens.radius.sm};
        color: rgba(255, 0, 0, 0.9);
        font-size: ${designTokens.fontSize.sm};
      ">${this.escapeHtml(this.state.error)}</div>`;
      
      return createContainer(
        heading + errorDisplay,
        { padding: 'lg', flexDirection: 'column' }
      );
    }
    
    // Plan features
    const freeFeatures = [
      '1 decision per month',
      'Explore reasoning process',
      'Share public link',
      'Basic cortex access (6 cortices)'
    ];
    
    const regularFeatures = [
      '10 decisions per month',
      'AI reasoning',
      'Web research',
      'PDF export',
      'Decision Sheets',
      'Extended cortex access (15 cortices)'
    ];
    
    const proFeatures = [
      'Unlimited decisions',
      'Full AI reasoning suite',
      'All cortices unlocked',
      'Advanced web research',
      'PDF export',
      'Decision Sheets',
      'Priority support',
      'Early access to new features'
    ];
    
    // Plan cards
    const freeCard = this.createPlanCard(
      'Free',
      '€0',
      '/month',
      freeFeatures,
      'free',
      null,
      false
    );
    
    const regularCard = this.createPlanCard(
      'Regular',
      '€20',
      '/month',
      regularFeatures,
      'regular',
      'Subscribe to Regular',
      this.state.loading
    );
    
    const proCard = this.createPlanCard(
      'Pro',
      '€200',
      '/month',
      proFeatures,
      'pro',
      'Subscribe to Pro',
      this.state.loading
    );
    
    const plansGrid = `
      <div style="
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: ${designTokens.padding.lg};
        margin-top: ${designTokens.padding.lg};
      ">
        ${freeCard}
        ${regularCard}
        ${proCard}
      </div>
    `;
    
    const scrollableContent = createScrollableContainer(
      plansGrid,
      { direction: 'vertical' }
    );
    
    return createContainer(
      heading + scrollableContent,
      { padding: 'xl', flexDirection: 'column' }
    );
  }

  private createPlanCard(
    name: string,
    price: string,
    period: string,
    features: string[],
    planSlug: string,
    buttonText: string | null,
    loading: boolean
  ): string {
    const featuresList = features.map(feature => `
      <li style="
        padding: ${designTokens.padding.xs} 0;
        color: ${designTokens.contentColor};
        font-size: ${designTokens.fontSize.sm};
        list-style: none;
        position: relative;
        padding-left: ${designTokens.padding.lg};
      ">
        <span style="position: absolute; left: 0;">✓</span>
        ${this.escapeHtml(feature)}
      </li>
    `).join('');
    
    const button = buttonText ? createButton(
      loading ? 'Loading...' : buttonText,
      {
        className: `pricing-subscribe-${planSlug}`,
        variant: 'primary',
        style: loading ? 'opacity: 0.6; cursor: not-allowed;' : ''
      }
    ) : '';
    
    return `
      <div style="
        background: ${designTokens.cardBackground};
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.md};
        padding: ${designTokens.padding.xl};
        display: flex;
        flex-direction: column;
      ">
        <h3 style="
          margin: 0 0 ${designTokens.padding.md} 0;
          font-size: ${designTokens.fontSize.xl};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">${this.escapeHtml(name)}</h3>
        <div style="
          margin: 0 0 ${designTokens.padding.lg} 0;
        ">
          <span style="
            font-size: ${designTokens.fontSize.xl};
            font-weight: 600;
            color: ${designTokens.contentColor};
          ">${this.escapeHtml(price)}</span>
          <span style="
            font-size: ${designTokens.fontSize.md};
            color: ${designTokens.contentColorMuted};
          ">${this.escapeHtml(period)}</span>
        </div>
        <ul style="
          margin: 0 0 ${designTokens.padding.lg} 0;
          padding: 0;
          flex: 1;
        ">
          ${featuresList}
        </ul>
        ${button}
      </div>
    `;
  }

  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Setup button handlers
    const subscribeRegularBtn = this.element.querySelector('.pricing-subscribe-regular') as HTMLButtonElement;
    const subscribeProBtn = this.element.querySelector('.pricing-subscribe-pro') as HTMLButtonElement;
    
    if (subscribeRegularBtn) {
      subscribeRegularBtn.addEventListener('click', () => {
        if (!this.state.loading) {
          this.process.subscribeToPlan('regular');
        }
      });
    }
    
    if (subscribeProBtn) {
      subscribeProBtn.addEventListener('click', () => {
        if (!this.state.loading) {
          this.process.subscribeToPlan('pro');
        }
      });
    }
  }

  updateState(newState: PricingState): void {
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('pricing', (id: string) => new PricingProcess(id));

